// LUMINA · pages/ProfileDetailPage.jsx
// Phase 104 — 档案详情: 报告 + 问事 + 日课

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { C, BG, BO } from '../constants/baziData';

const API = "/api";

export default function ProfileDetailPage({ user, lang }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [tab, setTab] = useState("reports");
  const [reports, setReports] = useState([]);
  const [oracleLogs, setOracleLogs] = useState([]);
  const [currentCycle, setCurrentCycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  const getHeaders = () => {
    const t = localStorage.getItem("lumina_token");
    return { "Authorization": `Bearer ${t || ""}`, "Content-Type": "application/json" };
  };

  useEffect(() => {
    if (!user || !id) return;
    loadAll();
  }, [user, id]);

  const loadAll = async () => {
    setLoading(true);
    const h = getHeaders();
    try {
      // Load profile detail
      const r1 = await fetch(`${API}/profiles/${id}`, { headers: h });
      if (!r1.ok) { setMsg("档案不存在"); setLoading(false); return; }
      setProfile(await r1.json());

      // Load reports
      const r2 = await fetch(`${API}/profiles/${id}/reports`, { headers: h });
      if (r2.ok) setReports(await r2.json());

      // Load oracle logs
      const r3 = await fetch(`${API}/profiles/${id}/oracle-logs`, { headers: h });
      if (r3.ok) setOracleLogs(await r3.json());

      // Load current cycle (if has birth data)
      const p = await r1.clone().json();
      if (p.birth_year) {
        try {
          const r4 = await fetch(`${API}/audit/current-cycle`, { headers: h });
          if (r4.ok) setCurrentCycle(await r4.json());
        } catch (e) { /* non-critical */ }
      }
    } catch (e) { setMsg(e.message); }
    setLoading(false);
  };

  if (!user) return <div style={{ padding: 40, color: C.mist3 }}>请先登录</div>;
  if (loading) return <div style={{ padding: 40, textAlign: "center", color: C.mist4 }}>Loading...</div>;
  if (!profile) return <div style={{ padding: 40, color: C.mist3 }}>{msg || "Not found"}</div>;

  const T = lang === "zh" ? {
    reports: "命盘报告", oracle: "问事记录", daily: "澄光日课",
    back: "← 返回档案列表", edit: "编辑档案",
    noReports: "暂无报告", noOracle: "暂无问事记录",
    score: "评分", view: "查看", date: "日期",
    dayun: "当前大运", yearScore: "流年能量", highEnergy: "高能量年份",
    needPro: "需要 Pro 订阅",
    free: "免费用户", pro: "Pro 会员",
    birth: "生辰", location: "出生地",
    searchPlaceholder: "搜索事项...", sortDate: "日期", sortScore: "评分", sortTopic: "主题",
    allConcerns: "全部事项", selectAll: "全选", batchDelete: "批量删除",
    batchDeleteConfirm: "确认删除", collapse: "收起", more: "更多",
    oracleSearchPlaceholder: "搜索问题...", sortModule: "模块", allModules: "全部模块",
  } : {
    reports: "Reports", oracle: "Oracle Logs", daily: "Daily Energy",
    back: "← Back to Profiles", edit: "Edit Profile",
    noReports: "No reports yet", noOracle: "No oracle queries yet",
    score: "Score", view: "View", date: "Date",
    dayun: "Current Dayun", yearScore: "Year Energy", highEnergy: "High Energy Year",
    needPro: "Pro subscription required",
    free: "Free", pro: "Pro",
    birth: "Birth", location: "Birthplace",
    searchPlaceholder: "Search concerns...", sortDate: "Date", sortScore: "Score", sortTopic: "Topic",
    allConcerns: "All Concerns", selectAll: "Select All", batchDelete: "Delete",
    batchDeleteConfirm: "Confirm delete", collapse: "Collapse", more: "more",
    oracleSearchPlaceholder: "Search queries...", sortModule: "Module", allModules: "All Modules",
  };

  const tabs = [
    { key: "reports", label: T.reports, count: reports.length },
    { key: "oracle", label: T.oracle, count: oracleLogs.length },
    { key: "daily", label: T.daily },
  ];

  return (
    <div style={{ minHeight: "100vh", background: C.bg, padding: "40px 20px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>

        {/* Back + Edit */}
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
          <button onClick={() => navigate("/profile/list")} style={{ ...BO, color: C.mist4, borderColor: C.mist4 }}>
            {T.back}
          </button>
          <button onClick={() => navigate("/profile/list")} style={{ ...BO, color: C.cyan, borderColor: C.cyan }}>
            {T.edit}
          </button>
        </div>

        {/* Profile Header */}
        <div style={{
          background: "rgba(255,255,255,.72)", border: "1px solid rgba(212,175,55,.16)",
          borderRadius: 12, padding: "20px 24px", marginBottom: 16, backdropFilter: "blur(12px)"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 28 }}>{profile.gender === "male" ? "👤" : "👩"}</span>
            <div>
              <h2 style={{ fontSize: 20, color: C.gold, margin: 0, fontFamily: "'Inter','Montserrat',sans-serif", fontWeight: 300 }}>
                {profile.name}
              </h2>
              <div style={{ fontSize: 12, color: C.mist4 }}>
                {T.birth}: {profile.birth_year}/{String(profile.birth_month).padStart(2, '0')}/{String(profile.birth_day).padStart(2, '0')}
                {profile.birth_hour >= 0 ? ` ${profile.birth_hour}:00` : ""}
                {profile.birth_location ? ` · ${T.location}: ${profile.birth_location}` : ""}
              </div>
            </div>
          </div>
          {profile.notes && <div style={{ fontSize: 12, color: C.mist4, marginTop: 4 }}>{profile.notes}</div>}
        </div>

        {/* Current Cycle Card */}
        {currentCycle && currentCycle.has_report && (
          <div style={{
            background: "rgba(212,175,55,.08)", border: "1px solid rgba(212,175,55,.2)",
            borderRadius: 10, padding: "14px 20px", marginBottom: 16
          }}>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 10, color: C.mist4, letterSpacing: ".06em" }}>{T.dayun}</div>
                <div style={{ fontSize: 16, color: C.gold, fontWeight: 500 }}>{currentCycle.dayun_gz || "—"}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: C.mist4, letterSpacing: ".06em" }}>{T.yearScore}</div>
                <div style={{ fontSize: 16, color: currentCycle.is_high_energy ? C.sage : C.mist2, fontWeight: 500 }}>
                  {currentCycle.year_score || "—"} {currentCycle.is_high_energy ? "⚡" : ""}
                </div>
              </div>
              {currentCycle.top_deity && (
                <div>
                  <div style={{ fontSize: 10, color: C.mist4, letterSpacing: ".06em" }}>修行建议</div>
                  <div style={{ fontSize: 14, color: C.gold, fontWeight: 500 }}>{currentCycle.top_deity}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: "flex", gap: 2, marginBottom: 16, borderBottom: `1px solid ${C.border}` }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              background: tab === t.key ? "rgba(6,78,59,.06)" : "transparent",
              border: "none", borderBottom: tab === t.key ? `2px solid ${C.gold}` : "2px solid transparent",
              padding: "10px 18px", cursor: "pointer", fontSize: 14, color: tab === t.key ? C.mist : C.mist4,
              fontWeight: tab === t.key ? 500 : 400, marginBottom: -1
            }}>
              {t.label}{t.count !== undefined && <span style={{ marginLeft: 6, fontSize: 11, color: C.mist4 }}>({t.count})</span>}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {tab === "reports" && (
          <ReportsTab profileId={id} T={T} navigate={navigate} />
        )}
        {tab === "oracle" && (
          <OracleTab profileId={id} T={T} />
        )}
        {tab === "daily" && (
          <DailyTab profile={profile} currentCycle={currentCycle} T={T} />
        )}
      </div>
    </div>
  );
}

// ── Tab: Reports ──

function ReportsTab({ profileId, T, navigate }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortVal, setSortVal] = useState("date");
  const [concern, setConcern] = useState("");
  const [concerns, setConcerns] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const hdrs = () => {
    const t = localStorage.getItem("lumina_token");
    return { "Authorization": `Bearer ${t || ""}`, "Content-Type": "application/json" };
  };

  const load = async (s, so, c) => {
    setLoading(true);
    const p = new URLSearchParams();
    if (s) p.set("search", s);
    if (so) p.set("sort", so);
    if (c) p.set("concern", c);
    try {
      const res = await fetch(`${API}/profiles/${profileId}/reports?${p}`, { headers: hdrs() });
      if (res.ok) {
        const data = await res.json();
        setReports(data);
        const uniq = [...new Set(data.map(r => r.concern_label).filter(Boolean))];
        setConcerns(prev => [...new Set([...prev, ...uniq])]);
      }
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { load("", "date", ""); }, []);

  const ctrl = {
    background: "rgba(255,255,255,.72)", border: "1px solid rgba(212,175,55,.2)",
    borderRadius: 6, padding: "7px 12px", fontSize: 13, color: C.mist, outline: "none",
  };

  // Group by birth+concern_label — API already returns newest-first
  const groups = [];
  const seenKeys = {};
  reports.forEach(r => {
    const key = `${r.birth}__${r.concern_label || ""}`;
    if (seenKeys[key] === undefined) { seenKeys[key] = groups.length; groups.push({ key, lead: r, rest: [] }); }
    else groups[seenKeys[key]].rest.push(r);
  });

  const allIds = reports.map(r => r.report_id);
  const allSelected = allIds.length > 0 && allIds.every(rid => selected.has(rid));
  const toggleSel = rid => setSelected(prev => { const n = new Set(prev); n.has(rid) ? n.delete(rid) : n.add(rid); return n; });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds));
  const toggleExp = key => setExpanded(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const batchDel = async () => {
    if (!selected.size) return;
    if (!window.confirm(`${T.batchDeleteConfirm} ${selected.size}?`)) return;
    setDeleting(true);
    try {
      await fetch(`${API}/profiles/${profileId}/reports/batch-delete`, {
        method: "POST", headers: hdrs(), body: JSON.stringify({ report_ids: [...selected] }),
      });
    } catch (_) {}
    setSelected(new Set());
    await load(search, sortVal, concern);
    setDeleting(false);
  };

  const renderCard = (r, nested) => (
    <div key={r.report_id} style={{
      background: nested ? "rgba(255,255,255,.5)" : "rgba(255,255,255,.72)",
      border: `1px solid rgba(212,175,55,${selected.has(r.report_id) ? ".4" : (nested ? ".06" : ".1")})`,
      borderRadius: nested ? 8 : 10, padding: nested ? "10px 14px" : "12px 16px",
      display: "flex", alignItems: "center", gap: 10,
    }}>
      <input type="checkbox" checked={!!selected.has(r.report_id)} onChange={() => toggleSel(r.report_id)} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: nested ? 12 : 13, color: C.mist }}>
          {r.created_at ? new Date(r.created_at).toLocaleDateString() : r.report_id}
        </div>
        <div style={{ fontSize: 11, color: C.mist4 }}>
          {r.birth} · {r.tier_label || "free"}
          {r.concern_label ? ` · ${r.concern_label}` : ""}
          {r.score != null ? ` · ${T.score}: ${r.score}` : ""}
        </div>
      </div>
      <button onClick={() => navigate(`/report/${r.report_id}`)} style={{
        background: "transparent", border: `0.5px solid ${C.gold}`, borderRadius: 6,
        padding: nested ? "4px 10px" : "5px 12px", fontSize: 11, color: C.gold, cursor: "pointer", flexShrink: 0,
      }}>{T.view}</button>
    </div>
  );

  return (
    <div>
      {/* Toolbar — stacks vertically on small screens via flexWrap */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === "Enter" && load(search, sortVal, concern)}
          placeholder={T.searchPlaceholder}
          style={{ ...ctrl, flex: "1 1 140px", minWidth: 100 }} />
        <select value={sortVal}
          onChange={e => { setSortVal(e.target.value); load(search, e.target.value, concern); }}
          style={{ ...ctrl, cursor: "pointer" }}>
          <option value="date">{T.sortDate}</option>
          <option value="score">{T.sortScore}</option>
          <option value="topic">{T.sortTopic}</option>
        </select>
        <select value={concern}
          onChange={e => { setConcern(e.target.value); load(search, sortVal, e.target.value); }}
          style={{ ...ctrl, cursor: "pointer" }}>
          <option value="">{T.allConcerns}</option>
          {concerns.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Batch action bar */}
      {allIds.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: C.mist4, cursor: "pointer" }}>
            <input type="checkbox" checked={allSelected} onChange={toggleAll} />
            {T.selectAll}
          </label>
          {selected.size > 0 && (
            <button onClick={batchDel} disabled={deleting}
              style={{ ...BO, color: C.stress, borderColor: C.stress, fontSize: 12, padding: "3px 10px" }}>
              {deleting ? "..." : `${T.batchDelete} (${selected.size})`}
            </button>
          )}
        </div>
      )}

      {loading && <div style={{ textAlign: "center", padding: 30, color: C.mist4 }}>Loading...</div>}
      {!loading && allIds.length === 0 && <EmptyTab msg={T.noReports} />}

      {!loading && allIds.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {groups.map(({ key, lead, rest }) => (
            <div key={key}>
              {renderCard(lead, false)}
              {rest.length > 0 && (
                <div style={{ marginLeft: 20, marginTop: 4 }}>
                  <button onClick={() => toggleExp(key)} style={{
                    background: "transparent", border: "none", fontSize: 11,
                    color: C.mist4, cursor: "pointer", padding: "3px 0",
                  }}>
                    {expanded.has(key) ? T.collapse : `+${rest.length} ${T.more}`}
                  </button>
                  {expanded.has(key) && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
                      {rest.map(r => renderCard(r, true))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Tab: Oracle ──

function OracleTab({ profileId, T }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortVal, setSortVal] = useState("date");
  const [moduleFilter, setModuleFilter] = useState("");

  const hdrs = () => {
    const t = localStorage.getItem("lumina_token");
    return { "Authorization": `Bearer ${t || ""}`, "Content-Type": "application/json" };
  };

  const load = async (s, so, m) => {
    setLoading(true);
    const p = new URLSearchParams();
    if (s) p.set("search", s);
    if (so) p.set("sort", so);
    if (m) p.set("module", m);
    try {
      const res = await fetch(`${API}/profiles/${profileId}/oracle-logs?${p}`, { headers: hdrs() });
      if (res.ok) setLogs(await res.json());
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { load("", "date", ""); }, []);

  const moduleLabels = {
    liuyao: "六爻", bazi: "八字", ziwei: "紫微", qimen: "奇门",
    dream: "解梦", almanac: "老黄历", lottery: "灵签", naming: "起名", dharma: "密法",
  };

  const ctrl = {
    background: "rgba(255,255,255,.72)", border: "1px solid rgba(212,175,55,.2)",
    borderRadius: 6, padding: "7px 12px", fontSize: 13, color: C.mist, outline: "none",
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === "Enter" && load(search, sortVal, moduleFilter)}
          placeholder={T.oracleSearchPlaceholder}
          style={{ ...ctrl, flex: "1 1 140px", minWidth: 100 }} />
        <select value={sortVal}
          onChange={e => { setSortVal(e.target.value); load(search, e.target.value, moduleFilter); }}
          style={{ ...ctrl, cursor: "pointer" }}>
          <option value="date">{T.sortDate}</option>
          <option value="module">{T.sortModule}</option>
        </select>
        <select value={moduleFilter}
          onChange={e => { setModuleFilter(e.target.value); load(search, sortVal, e.target.value); }}
          style={{ ...ctrl, cursor: "pointer" }}>
          <option value="">{T.allModules}</option>
          {Object.entries(moduleLabels).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {loading && <div style={{ textAlign: "center", padding: 30, color: C.mist4 }}>Loading...</div>}
      {!loading && logs.length === 0 && <EmptyTab msg={T.noOracle} />}

      {!loading && logs.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {logs.map((l, i) => (
            <div key={i} style={{
              background: "rgba(255,255,255,.72)", border: "1px solid rgba(212,175,55,.1)",
              borderRadius: 10, padding: "12px 16px"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: C.gold, background: C.goldGlow, padding: "1px 8px", borderRadius: 4 }}>
                  {moduleLabels[l.routed_module] || l.routed_module}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {l.user_feedback === "thumbs_up" && (
                    <span style={{ fontSize: 14, color: C.gold }}>👍</span>
                  )}
                  {l.user_feedback === "thumbs_down" && (
                    <span style={{ fontSize: 14, color: C.mist4 }}>👎</span>
                  )}
                  <span style={{ fontSize: 11, color: C.mist4 }}>
                    {l.created_at ? new Date(l.created_at).toLocaleDateString() : ""}
                  </span>
                </div>
              </div>
              <div style={{ fontSize: 13, color: C.mist, marginBottom: 4 }}>Q: {l.query_text}</div>
              <div style={{ fontSize: 12, color: C.mist3, lineHeight: 1.5 }}>
                {l.narrative ? l.narrative.slice(0, 200) + (l.narrative.length > 200 ? "..." : "") : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Tab: Daily Energy ──

function DailyTab({ profile, currentCycle, T }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile) return;
    const t = localStorage.getItem("lumina_token");
    if (!t) { setErr("no token"); setLoading(false); return; }
    const h = { Authorization: "Bearer " + t, "Content-Type": "application/json" };
    const body = JSON.stringify({
      birth_year: profile.birth_year, birth_month: profile.birth_month,
      birth_day: profile.birth_day, birth_hour: profile.birth_hour, gender: profile.gender
    });

    fetch(API + "/audit/daily-energy", { method: "POST", headers: h, body })
      .then(r => {
        if (!r.ok) throw new Error(r.status + " " + (r.statusText || ""));
        return r.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  }, [profile]);

  if (loading) return React.createElement('div', { style: { textAlign: "center", padding: 40, color: C.mist4 } }, "Loading...");
  if (err) return React.createElement('div', { style: { padding: 20, color: C.stress } }, "Error: " + err);
  if (!data) return React.createElement('div', { style: { padding: 20, color: C.mist4 } }, "No data");

  return (
    <div>
      {/* Energy Card */}
      <div style={{
        background: "rgba(255,255,255,.72)", borderRadius: 10,
        padding: "16px 20px", marginBottom: 12, textAlign: "center"
      }}>
        {/* Date + Day Pillar */}
        <div style={{ fontSize: 11, color: C.mist4, marginBottom: 8 }}>
          {data.date ? `📅 ${data.date} · ${data.day_pillar?.ganzhi || ""}` : ""}
        </div>

        {/* Score */}
        <div style={{ fontSize: 40, fontWeight: 700, color: C.gold, lineHeight: 1.1 }}>
          {data.energy_score || "?"}
        </div>

        {/* Label */}
        <div style={{ fontSize: 15, color: C.mist, fontWeight: 500, marginTop: 2 }}>
          {data.energy_label || ""}
        </div>

        {/* Shishen + Element context */}
        {(data.shishen_type || data.day_element) && (
          <div style={{ fontSize: 11, color: C.gold, marginTop: 4, opacity: 0.8 }}>
            日主{data.daymaster_element || ""} · 今日{data.day_element || ""}{data.shishen_type ? ` · ${data.shishen_type}` : ""}
          </div>
        )}

        {/* Keywords */}
        <div style={{ fontSize: 11, color: C.mist4, marginTop: 6 }}>
          {(data.keywords || []).slice(0, 3).join(" · ")}
        </div>

        {/* AI Narrative */}
        {data.narrative_zh && (
          <div style={{
            fontSize: 12, color: C.mist, lineHeight: 1.7,
            marginTop: 10, padding: "8px 12px",
            background: "rgba(212,175,55,.06)", borderRadius: 8,
            textAlign: "left"
          }}>
            {data.narrative_zh}
          </div>
        )}

        {/* Trial countdown */}
        {data.trial_days_remaining !== undefined && (
          <div style={{ fontSize: 10, color: C.mist4, marginTop: 8 }}>
            试用剩余 {data.trial_days_remaining} 天 · <a href="/pricing" style={{ color: C.gold, textDecoration: "none" }}>升级 Pro</a>
          </div>
        )}
      </div>

      {/* Dashboard link */}
      <a href="/dashboard" style={{
        fontSize: 12, color: C.gold, textAlign: "center", display: "block",
        textDecoration: "none", fontWeight: 500
      }}>
        查看30天前瞻与季度预览 →
      </a>
    </div>
  );
}

// ── Shared ──// ── Shared ──// ── Shared ──// ── Shared ──

function EmptyTab({ msg }) {
  return (
    <div style={{ textAlign: "center", padding: 40, color: C.mist4, fontSize: 14 }}>{msg}</div>
  );
}
